/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React from 'react';
import parse, { domToReact } from 'html-react-parser';
import { CodeSnippet } from '@carbon/react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { apiPath } from 'utils/navigation';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Markdown from './Markdown';
import { cleanUpKey, b64DecodeUnicode } from '../utils/common';
import { getTranslatedExtensions } from '../utils/translate';

let copyEnabled = false;
if (typeof document !== 'undefined' && document.queryCommandSupported) {
  copyEnabled = document.queryCommandSupported('copy');
}
function DocumentationPage({ apis, doc }) {
  const { t } = useTranslation();
  const copyText = (_, parm) => {
    if (parm) {
      const elementNode = document.getElementById(parm);

      if (!elementNode) {
        // Can't proceed
        return;
      }

      // select and copy
      const range = document.createRange();
      range.selectNodeContents(elementNode);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      // we've at least selected the text... but
      if (copyEnabled) {
        // we can also copy straight to the clipboard
        document.execCommand('copy');
        sel.removeAllRanges();
      }
    }
  };
  const getContent = (doc) => {
    if (doc && doc.content !== undefined && doc.content.length > 0) {
      return doc;
    }
    if (doc && doc.docs) {
      return getContent(doc.docs[0]);
    }
    return null;
  };

  // eslint-disable-next-line global-require
  const rmmd = require('remove-markdown');

  const parserOptions = {
    // eslint-disable-next-line react/no-unstable-nested-components
    replace: (domNode) => {
      if (!domNode.attribs) return;

      if (domNode.attribs.class === 'show-explorer-apis') {
        return (
          <div className="apic-explorer_apis">
            {apis.map((api, index) => {
              const summary = api.info && (api.info.summary || api.info['x-ibm-summary']);
              const description = api.info && api.info.description;
              return (
                <section key={`${api.id + index}`} className="apic-explorer_api">
                  <h3>
                    <Link to={`/${apiPath(api)}/overview`} className="api-title">
                      {getTranslatedExtensions(api.info, 'title')}
                    </Link>
                  </h3>
                  <caption className="cds--type-caption">{api.info.version}</caption>
                  <div className="apiDescription">{summary ? rmmd(summary) : description && rmmd(description)}</div>
                </section>
              );
            })}
          </div>
        );
      }
      if (domNode.attribs.class === 'show-explorer-codesnippet') {
        return (
          <CodeSnippet
            id={domNode.attribs.id}
            type="multi"
            onClick={(e) => {
              copyText(e, domNode.attribs.id);
            }}
            aria-label="Code Snippet Text"
          >
            {domToReact(domNode.children[0].data)}
          </CodeSnippet>
        );
      }
    }
  };

  let title = t('apic_connect_apis');

  if (doc && doc.title) {
    title = getTranslatedExtensions(doc, 'title');
  }

  let content = `<div class="apic-explorer-docpage_title">
        <h2 class="apic-explorer-docpage_title-text">
          <span class="explorer-doc--header">
            No content to display
          </span>
        </h2>
      </div>`;
  let htmlContent = null;
  let format = 'md';

  const docPage = getContent(doc);

  if (docPage) {
    const docformat = getTranslatedExtensions(docPage, 'format');

    // if base64 encoded assume html
    if (docformat === 'b64html') {
      htmlContent = b64DecodeUnicode(getTranslatedExtensions(docPage, 'content'));
      format = 'b64html';
    } else {
      content = getTranslatedExtensions(docPage, 'content');
    }
  }

  return (
    <div className={classNames('apiconnect-explorer', 'apic-explorer--docpage', `apic-explorer--docpage_${cleanUpKey(title)}`)}>
      {format === 'b64html' && htmlContent && parse(htmlContent, parserOptions)}
      {format === 'md' && content && <Markdown data={content} />}
    </div>
  );
}

DocumentationPage.propTypes = {
  options: PropTypes.object,
  apis: PropTypes.array,
  doc: PropTypes.object
};

export default DocumentationPage;
